import { useContext } from "react";
import { Fragment, ReactElement } from "react";
import { flatten, uniq } from "lodash";
import { useTranslation } from "react-i18next";
import { RESOURCE_NAME } from "common/authorization";
import { TranslationKeys } from "common/utils/i18n/fallbackEnTranslations";
import {
  FIRM_WARNING_NAME,
  FirmWarningTextConfig,
  FIRMWithWarnings,
  floodzoneWarningText,
} from "common-client/utils/firmInfoWarnings";
import { getFormattedFIRMNames } from "common-client/utils/firms";
import { AuthContext } from "../../../Authorization/AuthContext";
import {
  BANNER_TYPES,
  SecondaryMessageWrapper,
} from "../../../Common/__styles__/Banner";
import { Emphasis, StyledLink } from "../../../Common/__styles__/Typography";
import Banner from "../../../Common/Banner";
import { GetHelpLink } from "../../../Guest/GetHelpLink";
import TranslatedFIRMWarningMessage from "./TranslatedFIRMWarningMessage";

type PlaceholderToComponentMap = { [key: string]: ReactElement };

type RenderWarningProps = {
  splitMessage: Array<string>;
  placeholderToComponentMap: PlaceholderToComponentMap;
};

const renderFirmWarning = ({
  splitMessage,
  placeholderToComponentMap,
}: RenderWarningProps): ReactElement => {
  const firmWarning = splitMessage.map((part, index) => (
    <Fragment key={index}>
      {placeholderToComponentMap[part] ? placeholderToComponentMap[part] : part}
    </Fragment>
  ));

  return <span>{firmWarning}</span>;
};

const checkRequiredReplacements = ({
  requiredReplacements,
  placeholderToComponentMap,
}: {
  requiredReplacements?: Array<string>;
  placeholderToComponentMap: PlaceholderToComponentMap;
}) => {
  if (!requiredReplacements) return;

  requiredReplacements.forEach(replacement => {
    //This will catch any missing replacements if the function is called without a required one
    if (!placeholderToComponentMap[replacement]) {
      throw new Error(`Missing replacement for ${replacement}`);
    }
  });
};

const handleMessage = ({
  mainMessage,
  secondaryMessage,
  placeholderToComponentMap,
}: {
  mainMessage?: string | ReactElement;
  secondaryMessage?: string | ReactElement;
  placeholderToComponentMap: PlaceholderToComponentMap;
}) => {
  const formatMessage = (message: string) => {
    //This regex splits the default message into an array where each part is either a replacement or a string
    const regex = new RegExp(
      `(${Object.keys(placeholderToComponentMap).join("|")})`,
      "gm",
    );

    const splitMessage = message.split(regex);

    return renderFirmWarning({
      splitMessage,
      placeholderToComponentMap,
    });
  };
  if (typeof mainMessage === "string" && typeof secondaryMessage === "string") {
    const fullMessage = `${mainMessage} ${secondaryMessage}`;
    return formatMessage(fullMessage);
  } else if (typeof secondaryMessage === "string") {
    const secondaryMessageComponent = formatMessage(secondaryMessage);
    return (
      <>
        {mainMessage}
        <SecondaryMessageWrapper>
          {secondaryMessageComponent}
        </SecondaryMessageWrapper>
      </>
    );
  } else {
    return (
      <>
        {mainMessage}
        <SecondaryMessageWrapper>{secondaryMessage}</SecondaryMessageWrapper>
      </>
    );
  }
};

const getFirmWarningText = ({
  firmWarning,
  canUpdate,
  isViewOnly,
  isPublic,
  placeholderToComponentMap,
  warningTextConfig,
}: {
  firmWarning: FIRM_WARNING_NAME;
  canUpdate?: boolean;
  isPublic?: boolean;
  isViewOnly?: boolean;
  placeholderToComponentMap: PlaceholderToComponentMap;
  warningTextConfig: FirmWarningTextConfig;
}): ReactElement => {
  const { defaultMessage, canUpdateMessage, isPublicMessage, viewOnlyMessage } =
    warningTextConfig[firmWarning];

  checkRequiredReplacements({
    requiredReplacements: defaultMessage.requiredReplacements,
    placeholderToComponentMap,
  });

  const mainMessage = defaultMessage.key
    ? TranslatedFIRMWarningMessage({
        translationKey: defaultMessage.key,
        placeholderToComponentMap,
      })
    : defaultMessage.message;

  let message;
  if (canUpdate) {
    checkRequiredReplacements({
      requiredReplacements: canUpdateMessage.requiredReplacements,
      placeholderToComponentMap,
    });

    message = canUpdateMessage.message;
  } else if (isPublic && isPublicMessage) {
    checkRequiredReplacements({
      requiredReplacements: isPublicMessage.requiredReplacements,
      placeholderToComponentMap,
    });

    message = isPublicMessage.key
      ? TranslatedFIRMWarningMessage({
          translationKey: isPublicMessage.key as TranslationKeys,
          placeholderToComponentMap,
        })
      : isPublicMessage.message;
  } else if (isViewOnly && viewOnlyMessage) {
    checkRequiredReplacements({
      requiredReplacements: viewOnlyMessage.requiredReplacements,
      placeholderToComponentMap,
    });

    message = viewOnlyMessage.message;
  }

  return handleMessage({
    mainMessage,
    secondaryMessage: message,
    placeholderToComponentMap,
  });
};

export const FIRMWarningsBanner = ({
  firms,
  propertyId,
  warningTextConfig,
  title,
  additionalReplacements,
}: {
  firms: Array<
    Pick<FIRMWithWarnings, "warnings" | "isApproximateBfe" | "name" | "mscLink">
  >;
  propertyId?: string;
  warningTextConfig: FirmWarningTextConfig;
  title?: string;
  additionalReplacements?: PlaceholderToComponentMap;
  compactList?: boolean;
}) => {
  const { t } = useTranslation();
  const { authorized, isPublic } = useContext(AuthContext);

  const warningNames = uniq(
    flatten(firms.map(firm => firm.warnings)).map(warning => warning.name),
  );

  const canUpdateFIRMInfo = authorized({
    resource: RESOURCE_NAME.FIRM,
    permission: "update",
  });

  const canViewFIRMInfo = authorized({
    resource: RESOURCE_NAME.FIRM,
    permission: "view",
  });

  const mscLink = firms.find(firm =>
    firm.warnings.some(
      warning =>
        warning.name === FIRM_WARNING_NAME.PROPERTY_HAS_APPROXIMATE_BFE,
    ),
  )?.mscLink;

  const warningsInBanner = uniq(warningNames).map(warningName => {
    const formatedFirmNames = getFormattedFIRMNames(firms, warningName);
    const floodzoneText = floodzoneWarningText(firms);

    return getFirmWarningText({
      firmWarning: warningName,
      canUpdate: canUpdateFIRMInfo,
      isPublic,
      isViewOnly: canViewFIRMInfo && !isPublic,
      placeholderToComponentMap: {
        "{GET_HELP_LINK}": <GetHelpLink propertyId={propertyId} />,
        "{FLOODZONE_TEXT}": <span>{floodzoneText}</span>,
        "{FIRM_NAME}": <Emphasis>{formatedFirmNames}</Emphasis>,
        "{MSC_LINK}": mscLink ? (
          <StyledLink href={mscLink} target="_blank">
            Flood Insurance Study
          </StyledLink>
        ) : (
          <Emphasis>Flood Insurance Study</Emphasis>
        ),
        ...additionalReplacements,
      },
      warningTextConfig,
    });
  });

  if (!warningsInBanner.length) return null;

  return (
    <Banner
      type={BANNER_TYPES.ATTENTION}
      title={title && t("property-flood-info-warning-header", { title })}
      items={warningsInBanner}
      testId="firm-warning-banner"
    />
  );
};
